import React, { FC, useEffect, useRef, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import {
  LuBuilding2,
  LuHash,
  LuAppWindow,
  LuPlus,
  LuPencil,
} from 'react-icons/lu';
import { MdOutlineDns } from 'react-icons/md';

import { Modal } from '@/components/elements/Modal';
import ConfigInput from '@/components/elements/ConfigInput';
import ConfigDropdown from '@/components/elements/ConfigDropdown';

import { OPERATOR_PROPS } from '@/constant/operator/operator.constant';
import { tid } from '@/utils/testId';
import { OPERATOR_TEST_IDS as TID } from '@/constant/testIds/operator.testIds';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { COMMON_VALIDATION_RULES } from '@/constant/global/global.constant';
import { getCustomisedValidationRules } from '@/utils/generic';
import { trimSpacesInPayload } from '@/utils/stringFormatting';
import { useDebounce } from '@/hooks/useDebounce';

import { getProviders, selectedProvider } from '@/store/slices/provider.slice';
import {
  getSubApplications,
  selectedSubApplication,
} from '@/store/slices/subApplication.slice';
import { createOperator, updateOperator } from '@/store/slices/operator.slice';
import { getOperatorDetail } from '@/store/selectors/operator.selector';
import { providerList } from '@/store/selectors/providers.selector';
import { subApplicationList } from '@/store/selectors/subApplication.selector';

import {
  handleOperatorApplicationId,
  handleOperatorProviderId,
} from '@/constant/operator/operator.error';
import { ICreateOperatorProps as ICreateOperatorPayload } from '@/store/sagas/operatorSaga';
import {
  ICreateOperatorFormInputs,
  ISingleOperatorData,
} from './operator.interface';

export interface IOperatorModalProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  /** When provided the modal operates in Edit mode */
  operatorData?: ISingleOperatorData | null;
}

const OperatorModal: FC<IOperatorModalProps> = ({
  isOpen,
  onClose,
  operatorData,
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!operatorData;

  const { loading, error } = useSelector(getOperatorDetail);

  // Tracks whether THIS modal triggered the in-flight request
  const pendingRef = useRef(false);

  const [providerId, setProviderId] = useState('');
  const [subapplicationId, setSubapplicationId] = useState('');
  const [initialProviderId, setInitialProviderId] = useState('');
  const [initialSubapplicationId, setInitialSubapplicationId] = useState('');
  const [searchProvider, setSearchProvider] = useState('');
  const [searchApplication, setSearchApplication] = useState('');
  const [isProviderSelected, setIsProviderSelected] = useState(false);
  const [isSubAppIdSelected, setIsSubAppIdSelected] = useState(false);

  const searchProviderQuery = useDebounce(
    searchProvider,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const searchApplicationQuery = useDebounce(
    searchApplication,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  const providers = useSelector(providerList).filter(
    (p) => p.name !== 'All Providers',
  );
  const subapplication = useSelector(subApplicationList).filter(
    (s) => s.name !== 'All SubApplications',
  );

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors, isValid, isSubmitted, dirtyFields },
  } = useForm<ICreateOperatorFormInputs>({ mode: 'onChange' });

  const isLoading = pendingRef.current && loading;
  const isDropdownDirty =
    providerId !== initialProviderId || subapplicationId !== initialSubapplicationId;
  const isSaveDisabled =
    !isValid ||
    !isProviderSelected ||
    !isSubAppIdSelected ||
    isLoading ||
    (isEditMode && isEmpty(dirtyFields) && !isDropdownDirty);

  /* ── Auto-close when the API responds ───────────────────────────────── */
  useEffect(() => {
    if (pendingRef.current && !loading) {
      pendingRef.current = false;
      if (!error) {
        onClose(false); // success -> close
      }
      // on error -> stay open so user can retry
    }
  }, [loading]);

  /* ── Hydrate edit mode ───────────────────────────────────────────────── */
  useEffect(() => {
    if (isOpen && isEditMode && operatorData) {
      reset({
        name: operatorData.name?.trim() ?? '',
        sourceId: operatorData.sourceId?.trim() ?? '',
      });
      setProviderId(operatorData.providerId ?? '');
      setSubapplicationId(operatorData.subapplicationId ?? '');
      setInitialProviderId(operatorData.providerId ?? '');
      setInitialSubapplicationId(operatorData.subapplicationId ?? '');
      setIsProviderSelected(!!operatorData.providerId);
      setIsSubAppIdSelected(!!operatorData.subapplicationId);
    }
  }, [isOpen, operatorData]);

  /* ── Reset on close ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) {
      pendingRef.current = false;
      reset();
      clearErrors();
      setSearchApplication('');
      setSearchProvider('');
      setSubapplicationId('');
      setProviderId('');
      setInitialProviderId('');
      setInitialSubapplicationId('');
      setIsProviderSelected(false);
      setIsSubAppIdSelected(false);
    }
  }, [isOpen]);

  /* ── Fetch providers ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    const query = searchProviderQuery?.trim() ?? '';
    if (query.length > 0 && query.length < 3) return;

    dispatch(
      getProviders({
        limit: 100,
        page: 1,
        keywords: query.length >= 3 ? query : undefined,
        partnerId: '',
      }),
    );
  }, [isOpen, searchProviderQuery]);

  /* ── Fetch sub-apps ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    const query = searchApplicationQuery?.trim() ?? '';
    if (query.length > 0 && query.length < 3) return;

    dispatch(
      getSubApplications({
        limit: 100,
        page: 1,
        keywords: query.length >= 3 ? query : undefined,
      }),
    );
  }, [isOpen, searchApplicationQuery]);

  /* ── Dropdown handlers ───────────────────────────────────────────────── */
  const handleSelectedProvider = (value: any) => {
    const id = value?.id;
    setProviderId(id ?? '');
    setIsProviderSelected(!isEmpty(id));
    if (!isEmpty(id)) dispatch(selectedProvider(value));
  };

  const handleSubapplicationId = (value: any) => {
    const id = value?.id;
    setSubapplicationId(id ?? '');
    setIsSubAppIdSelected(!isEmpty(id));
    if (!isEmpty(id)) dispatch(selectedSubApplication(value));
  };

  /* ── Submit ──────────────────────────────────────────────────────────── */
  const onSubmit: SubmitHandler<ICreateOperatorFormInputs> = (formData) => {
    if (!providerId || !subapplicationId) return;
    pendingRef.current = true;
    if (isEditMode) {
      dispatch(
        updateOperator({
          id: operatorData!.operatorId,
          updatePayload: trimSpacesInPayload({
            name: formData.name,
            sourceId: formData.sourceId,
            providerId,
            subapplicationId,
          }),
        } as any),
      );
    } else {
      dispatch(
        createOperator(
          trimSpacesInPayload({
            name: formData.name,
            sourceId: formData.sourceId,
            providerId,
            subapplicationId,
          }) as ICreateOperatorPayload,
        ),
      );
    }
  };

  /* ── Derived selected objects for edit-mode dropdowns ────────────────── */
  const selectedProviderObj =
    isEditMode && operatorData
      ? (providers.find(
        (p) => p.id === (providerId || operatorData.providerId),
      ) ?? {
        id: operatorData.providerId,
        name: operatorData.provider?.name ?? '',
        profile: operatorData.provider?.name ?? '',
      })
      : undefined;

  const selectedSubAppObj =
    isEditMode && operatorData
      ? (subapplication.find(
        (s) => s.id === (subapplicationId || operatorData.subapplicationId),
      ) ?? {
        id: operatorData.subapplicationId,
        name: operatorData.subapplication?.name ?? '',
      })
      : undefined;

  // Block backdrop / X / Cancel while request is in flight
  const handleClose = () => {
    if (!isLoading) onClose(false);
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="2xl">
      <Modal.Header
        title={
          isEditMode
            ? OPERATOR_PROPS.EDIT_OPERATOR
            : OPERATOR_PROPS.CREATE_OPERATOR
        }
        subtitle={
          isEditMode
            ? OPERATOR_PROPS.MODAL.EDIT_SUBTITLE
            : OPERATOR_PROPS.MODAL.CREATE_SUBTITLE
        }
        icon={<LuBuilding2 className="w-4 h-4" />}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="flex flex-col gap-3">
          <ConfigInput
            dataTestId={tid(TID.MODAL_NAME_INPUT)}
            label={OPERATOR_PROPS.OPERATOR_NAME.LABEL}
            placeholder={OPERATOR_PROPS.OPERATOR_NAME.PLACEHOLDER}
            icon={LuBuilding2}
            required
            register={register}
            name="name"
            formValidationEnabled
            validationSchema={getCustomisedValidationRules({
              allowWhiteSpacesInBetween: true,
            })}
            error={!!errors?.name}
            errorMsg={errors?.name?.message}
          />

          <ConfigInput
            dataTestId={tid(TID.MODAL_SOURCE_ID_INPUT)}
            label={OPERATOR_PROPS.OPERATOR_ID.LABEL}
            placeholder={OPERATOR_PROPS.OPERATOR_ID.PLACEHOLDER}
            icon={LuHash}
            required
            register={register}
            name="sourceId"
            formValidationEnabled
            validationSchema={COMMON_VALIDATION_RULES}
            error={!!errors?.sourceId}
            errorMsg={errors?.sourceId?.message}
          />

          <ConfigDropdown
            dataTestId={tid(TID.MODAL_PROVIDER_SELECT)}
            label={OPERATOR_PROPS.PROVIDER_ID.LABEL}
            placeholder={OPERATOR_PROPS.PROVIDER_ID.PLACEHOLDER}
            value={providerId}
            dynamicLists={providers}
            nameKey="profile"
            handleSearch={setSearchProvider}
            onDynamicSelect={handleSelectedProvider}
            selectedValue={selectedProviderObj}
            isRequired
            error={isSubmitted && !isProviderSelected}
            errorMessage={
              isSubmitted && !isProviderSelected
                ? (handleOperatorProviderId(providerId) ?? undefined)
                : undefined
            }
            enableSearch
            icon={MdOutlineDns}
          />

          <ConfigDropdown
            dataTestId={tid(TID.MODAL_SUBAPP_SELECT)}
            label={OPERATOR_PROPS.SUb_APPLICATION_ID.LABEL}
            placeholder={OPERATOR_PROPS.SUb_APPLICATION_ID.PLACEHOLDER}
            value={subapplicationId}
            dynamicLists={subapplication}
            handleSearch={setSearchApplication}
            onDynamicSelect={handleSubapplicationId}
            selectedValue={selectedSubAppObj}
            isRequired
            error={isSubmitted && !isSubAppIdSelected}
            errorMessage={
              isSubmitted && !isSubAppIdSelected
                ? (handleOperatorApplicationId(subapplicationId) ?? undefined)
                : undefined
            }
            enableSearch
            icon={LuAppWindow}
          />
        </Modal.Body>

        <Modal.Footer>
          <Modal.CancelButton
            data-testid={tid(TID.MODAL_CANCEL_BTN)}
            onClick={handleClose}
            disabled={isLoading}
          >
            {OPERATOR_PROPS.MODAL.CANCEL}
          </Modal.CancelButton>
          <Modal.ConfirmButton
            data-testid={tid(TID.MODAL_SAVE_BTN)}
            type="submit"
            disabled={isSaveDisabled}
            loading={isLoading}
            icon={
              isEditMode ? (
                <LuPencil className="w-4 h-4" />
              ) : (
                <LuPlus className="w-4 h-4" />
              )
            }
          >
            {isEditMode
              ? OPERATOR_PROPS.BUTTON.EDIT
              : OPERATOR_PROPS.BUTTON.SAVE}
          </Modal.ConfirmButton>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default OperatorModal;
